import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useLenis } from "@/hooks/useLenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Blogs from "@/pages/Blogs";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import ServicesPage from "@/pages/Services";
import PricingPage from "@/pages/Pricing";
import FaqPage from "@/pages/Faq";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import DataSecurityPolicy from "@/pages/DataSecurityPolicy";
import DataRetentionPolicy from "@/pages/DataRetentionPolicy";
import RefundPolicy from "@/pages/RefundPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import GrievancePolicy from "@/pages/GrievancePolicy";
import Registration from "@/pages/Registration";
import RegistrationServiceDetail from "@/pages/RegistrationServiceDetail";
import Taxation from "@/pages/Taxation";
import TaxationCategoryHub from "@/pages/TaxationCategoryHub";
import TaxationServiceDetail from "@/pages/TaxationServiceDetail";
import Compliance from "@/pages/Compliance";
import ComplianceServiceDetail from "@/pages/ComplianceServiceDetail";
import CategoryHub from "@/pages/CategoryHub";
import CategoryServiceDetail from "@/pages/CategoryServiceDetail";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Upload from "@/pages/Upload";
import Account from "@/pages/Account";
import AdminDashboard from "@/pages/AdminDashboard";

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const CanonicalRouteManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const BASE_URL = "https://www.biz360compliance.com";
    let p = pathname || "/";
    if (p.length > 1 && p.endsWith("/")) {
      p = p.slice(0, -1);
    }
    const canonicalUrl = `${BASE_URL}${p === "/" ? "/" : p}`;

    let canonicalEl = document.getElementById("biz360-canonical") || document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement("link");
      canonicalEl.id = "biz360-canonical";
      canonicalEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute("href", canonicalUrl);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", canonicalUrl);
    }
  }, [pathname]);
  return null;
};

function App() {
  useLenis();

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <CanonicalRouteManager />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/registration/:slug" element={<RegistrationServiceDetail />} />
            <Route path="/taxation" element={<Taxation />} />
            <Route path="/taxation/:category" element={<TaxationCategoryHub />} />
            <Route path="/taxation/:category/:slug" element={<TaxationServiceDetail />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/compliance/:slug" element={<ComplianceServiceDetail />} />
            <Route path="/business-licences" element={<CategoryHub categorySlug="business-licences" />} />
            <Route path="/business-licences/:slug" element={<CategoryServiceDetail categorySlug="business-licences" />} />
            <Route path="/accounting-finance" element={<CategoryHub categorySlug="accounting-finance" />} />
            <Route path="/accounting-finance/:slug" element={<CategoryServiceDetail categorySlug="accounting-finance" />} />
            <Route path="/other-services" element={<CategoryHub categorySlug="other-services" />} />
            <Route path="/other-services/:slug" element={<CategoryServiceDetail categorySlug="other-services" />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/data-security-policy" element={<DataSecurityPolicy />} />
            <Route path="/data-security" element={<DataSecurityPolicy />} />
            <Route path="/data-retention-policy" element={<DataRetentionPolicy />} />
            <Route path="/data-retention" element={<DataRetentionPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/refund-cancellation-policy" element={<RefundPolicy />} />
            <Route path="/refunds" element={<RefundPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/grievance-redressal-policy" element={<GrievancePolicy />} />
            <Route path="/grievance-policy" element={<GrievancePolicy />} />
            <Route path="/grievance" element={<GrievancePolicy />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route
              path="/upload"
              element={
                <ProtectedRoute clientOnly>
                  <Upload />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute clientOnly>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute adminOnly>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
